import { createPortal } from 'react-dom';
import { X, Eye, Clock, UserCircle2, Activity } from 'lucide-react';
import type { DailyRecordWithUser } from '@/services/attendance.service';

// ── Helpers ────────────────────────────────────────────────────────────────────

function toLocalTime(iso: string | null): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  });
}

function formatHours(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return '–';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string, { label: string; cls: string }> = {
    present:        { label: 'Present',       cls: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700' },
    late:           { label: 'Late',          cls: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700' },
    half_day:       { label: 'Half Day',      cls: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700' },
    work_from_home: { label: 'WFH',           cls: 'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/40 dark:text-teal-300 dark:border-teal-700' },
    absent:         { label: 'Absent',        cls: 'bg-red-100 text-red-600 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700' },
    holiday:        { label: 'Holiday',       cls: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700' },
    leave:          { label: 'On Leave',      cls: 'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/40 dark:text-violet-300 dark:border-violet-700' },
  };
  const { label, cls } = cfg[status] ?? { label: status, cls: 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700' };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      {label}
    </span>
  );
}

// ── Props ──────────────────────────────────────────────────────────────────────

interface Props {
  record: DailyRecordWithUser;
  date: string;
  onClose: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function AttendanceSummaryModal({ record, date, onClose }: Props) {
  const fullName = [record.first_name, record.last_name].filter(Boolean).join(' ') || '–';

  const displayDate = new Date(date + 'T00:00:00').toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });

  // Derived values
  const workMins   = record.total_working_minutes ?? 0;
  const effectMins = record.effective_working_minutes ?? 0;
  const breakMins  = record.break_minutes ?? 0;

  // Net working hours progress (out of 480 min = 8h)
  const TARGET_MINS = 480;
  const pct = Math.min(100, Math.round((effectMins / TARGET_MINS) * 100));

  const infoGrid = [
    { label: 'FIRST PUNCH IN',    value: toLocalTime(record.first_punch_in),  bg: 'bg-emerald-50 border-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-800', val: 'text-emerald-700 dark:text-emerald-300' },
    { label: 'LAST PUNCH OUT',    value: toLocalTime(record.last_punch_out),  bg: 'bg-rose-50 border-rose-100 dark:bg-rose-900/20 dark:border-rose-800',             val: 'text-rose-700 dark:text-rose-300'       },
    { label: 'PUNCH HOURS',       value: formatHours(workMins),               bg: 'bg-slate-50 border-slate-100 dark:bg-muted/50 dark:border-border',         val: 'text-slate-700 dark:text-slate-200'     },
    { label: 'BREAK TIME',        value: formatHours(breakMins) || '–',       bg: 'bg-teal-50 border-teal-100 dark:bg-teal-900/20 dark:border-teal-800',                 val: 'text-teal-700 dark:text-teal-300'         },
    { label: 'BULK HOURS',        value: formatHours(record.effective_working_minutes), bg: 'bg-slate-50 border-slate-100 dark:bg-muted/50 dark:border-border', val: 'text-slate-700 dark:text-slate-200'     },
    { label: 'GRACE',             value: (record.grace_minutes ?? 0) > 0 ? `+${formatHours(record.grace_minutes)}` : '–', bg: 'bg-teal-50 border-teal-100 dark:bg-teal-900/20 dark:border-teal-800', val: (record.grace_minutes ?? 0) > 0 ? 'text-teal-700 dark:text-teal-300' : 'text-slate-400 dark:text-slate-500' },
    { label: 'PENALTY',           value: (record.penalty_approved_minutes ?? 0) > 0 ? formatHours(record.penalty_approved_minutes) : '–', bg: 'bg-rose-50 border-rose-100 dark:bg-rose-900/20 dark:border-rose-800', val: (record.penalty_approved_minutes ?? 0) > 0 ? 'text-rose-600 dark:text-rose-300' : 'text-slate-400 dark:text-slate-500' },
  ];

  return createPortal(
    /* Overlay */
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Panel */}
      <div className="relative w-full max-w-lg rounded-2xl border border-slate-200 dark:border-border bg-white dark:bg-card shadow-2xl">

        {/* Header */}
        <div className="flex items-center gap-3 border-b border-slate-100 dark:border-border/60 px-5 py-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-50 dark:bg-teal-900/30 border border-teal-100 dark:border-teal-800">
            <Eye className="h-4.5 w-4.5 text-teal-600 dark:text-teal-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">Attendance Summary</h2>
            <p className="text-xs text-slate-400 dark:text-slate-500">{displayDate}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-muted hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Employee card */}
        <div className="flex items-center gap-3 border-b border-slate-100 dark:border-border/60 bg-slate-50/60 dark:bg-muted/40 px-5 py-3.5">
          <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-slate-200 dark:border-border bg-slate-100 dark:bg-muted">
            {record.photo_url ? (
              <img src={record.photo_url} alt={fullName} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <UserCircle2 className="h-6 w-6 text-slate-400 dark:text-slate-500" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-bold uppercase tracking-wide text-slate-800 dark:text-slate-100 text-sm">{fullName}</span>
              <StatusBadge status={record.status} />
            </div>
            <span className="text-xs text-slate-400 dark:text-slate-500">{record.employee_code ?? '–'}</span>
          </div>
        </div>

        {/* Info grid */}
        <div className="grid grid-cols-2 gap-2.5 p-5 pb-3">
          {infoGrid.map((item) => (
            <div
              key={item.label}
              className={`rounded-xl border p-3 ${item.bg}`}
            >
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1">
                {item.label}
              </p>
              <p className={`text-base font-bold ${item.val}`}>{item.value}</p>
            </div>
          ))}
        </div>

        {/* Net working hours bar */}
        <div className="px-5 pb-2">
          <div className="rounded-xl border border-slate-100 dark:border-border bg-slate-50 dark:bg-muted/50 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                NET WORKING HOURS
              </span>
              <span className="text-xs font-bold text-teal-700 dark:text-teal-400">{formatHours(effectMins)}</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
              <div
                className="h-full rounded-full bg-teal-500 transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="mt-1 text-right text-[10px] text-slate-400 dark:text-slate-500">{pct}% of 8h</p>
          </div>
        </div>

        {/* Footer: total punches */}
        <div className="flex items-center justify-between border-t border-slate-100 dark:border-border/60 px-5 py-3">
          <div className="flex items-center gap-2">
            <Activity className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
            <span className="text-xs text-slate-500 dark:text-slate-400">Total Punches</span>
            <span className="inline-flex items-center rounded-full bg-slate-700 dark:bg-slate-600 px-2.5 py-0.5 text-xs font-bold text-white">
              {record.punch_count ?? 0}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
            <Clock className="h-3.5 w-3.5" />
            <span>Work mode: {record.work_mode ?? 'office'}</span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
